import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import {
  offerConverter,
  operatingConditionBaseConverter,
} from '@seu-converter';
import { SEU_NEW_ID } from '@seu-helpers';
import {
  OfferFormService,
  RequestService,
  StoreService,
  ToastService,
} from '@seu-services';
import {
  SEU_API_ENDPOINTS,
  SeuInterconnection,
  SeuOffer,
  SeuOfferApi,
  SeuOperatingCondition,
  SeuOperatingConditionBase,
} from '@seu-types';
import { catchError, firstValueFrom, of } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class OperatingConditionService {
  constructor(
    private offerFormService: OfferFormService,
    private store: StoreService,
    private requestService: RequestService,
    private toastService: ToastService,
    private translateService: TranslateService
  ) {}

  public getOperatingConditionFromOffer(
    offer: SeuOffer,
    operatingConditionId: string
  ): SeuOperatingCondition | undefined {
    let operatingCondition = undefined;

    offer.interconnections.forEach((interconnection) => {
      interconnection.operatingConditions.forEach((oc) => {
        if (oc.id === operatingConditionId) {
          operatingCondition = oc;
        }
      });
    });

    return operatingCondition;
  }

  public getInterconnectionByOperatingConditionId(
    offer: SeuOffer,
    operatingConditionId: string
  ): SeuOperatingCondition | undefined {
    let interconnection = undefined;

    offer.interconnections.forEach((ic) => {
      ic.operatingConditions.forEach((oc) => {
        if (oc.id === operatingConditionId) {
          interconnection = ic;
        }
      });
    });

    return interconnection;
  }

  public async createOperatingCondition(
    offerId: string,
    interconnectionId: string,
    data: SeuOperatingConditionBase
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingCondition
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId);

    const dataApi = operatingConditionBaseConverter.toApi(data);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, dataApi)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.create-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.create-success',
      // });
    }
  }

  public async updateOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string,
    data: SeuOperatingConditionBase
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const dataApi = operatingConditionBaseConverter.toApi(data);

    const responseData = await firstValueFrom(
      this.requestService
        .put<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, dataApi)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.save-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.save-success',
      // });
    }
  }

  public async deleteOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDetail
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const responseData = await firstValueFrom(
      this.requestService
        .delete<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.delete-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.store.updateActiveInterconnection(
        updatedOffer.interconnections.find(
          (item) => item.id === interconnectionId
        ) ?? null
      );
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.delete-success',
      // });
    }
  }

  public initNewOperatingCondition(
    offer: SeuOffer,
    interconnection: SeuInterconnection
  ): void {
    const newOperatingCondition: SeuOperatingCondition = {
      id: SEU_NEW_ID,
      title: this.translateService.instant(
        'global.untitled-operating-condition'
      ),
      description: '',
      categories: [],
      inputStreams: [],
      active: false,
    };

    const newInterconnection = {
      ...interconnection,
      operatingConditions: [
        ...interconnection.operatingConditions,
        newOperatingCondition,
      ],
    };

    const interconnections = offer.interconnections.map((item) =>
      item.id === interconnection.id ? newInterconnection : item
    );

    this.store.setActiveOffer({
      ...offer,
      interconnections,
    });

    this.offerFormService.init({
      ...offer,
      interconnections,
    });
  }

  public async duplicateOperatingCondition(
    offerId: string,
    interconnectionId: string,
    operatingConditionId: string
  ): Promise<void> {
    const endpoint = SEU_API_ENDPOINTS.operatingConditionDuplicate
      .replace(':offerId', offerId)
      .replace(':interconnectionId', interconnectionId)
      .replace(':operatingConditionId', operatingConditionId);

    const responseData = await firstValueFrom(
      this.requestService
        .post<SeuOfferApi>(endpoint as SEU_API_ENDPOINTS, null)
        .pipe(
          catchError(() => {
            this.toastService.addToast({
              type: 'error',
              text: 'operating-condition.toasts.duplicate-error',
            });

            return of(null);
          })
        )
    );

    if (responseData) {
      const updatedOffer = offerConverter.fromApi(responseData);
      this.store.setActiveOffer(updatedOffer);
      this.offerFormService.init(updatedOffer);
      // this.toastService.addToast({
      //   type: 'success',
      //   text: 'operating-condition.toasts.duplicate-success',
      // });
    }
  }
}
